import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  LiveClassService,
  PastSession,
} from '../../core/state/live-class/live-class.service';
import { DateTime } from 'luxon';

export interface SessionSelection {
  meetingId: string | null;
  sessionDate: Date | null;
  label: string;
}

@Component({
  selector: 'black-session-picker',
  standalone: false,
  templateUrl: './session-picker.component.html',
  styleUrl: './session-picker.component.css',
})
export class SessionPickerComponent implements OnInit, OnChanges {
  private liveClassService = inject(LiveClassService);

  @Input()
  liveClassId: string | null = null;

  @Output()
  sessionSelected = new EventEmitter<SessionSelection>();

  pastSessions: PastSession[] = [];
  loading = false;

  sessionControl = new FormControl<string>('all');

  readonly ALL_SESSIONS_VALUE = 'all';

  ngOnInit(): void {
    this.loadPastSessions();
    this.sessionControl.valueChanges.subscribe((value) => {
      this.onSelectionChange(value);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['liveClassId'] && !changes['liveClassId'].firstChange) {
      this.loadPastSessions();
    }
  }

  loadPastSessions(): void {
    if (!this.liveClassId) return;

    this.loading = true;
    this.liveClassService.getPastSessions(this.liveClassId).subscribe({
      next: (sessions) => {
        this.pastSessions = sessions;
        this.loading = false;
      },
      error: () => {
        this.pastSessions = [];
        this.loading = false;
      },
    });
  }

  onSelectionChange(value: string | null): void {
    if (!value || value === this.ALL_SESSIONS_VALUE) {
      this.sessionSelected.emit({
        meetingId: null,
        sessionDate: null,
        label: 'All Sessions',
      });
      return;
    }

    const separatorIndex = value.indexOf('|');
    const meetingId = value.substring(0, separatorIndex);
    const dateStr = value.substring(separatorIndex + 1);
    const session = this.pastSessions.find(
      (s) => s.meetingId === meetingId && new Date(s.sessionDate).toISOString() === dateStr
    );

    if (session) {
      this.sessionSelected.emit({
        meetingId: session.meetingId,
        sessionDate: new Date(session.sessionDate),
        label: this.getSessionLabel(session),
      });
    }
  }

  getSessionValue(session: PastSession): string {
    return `${session.meetingId}|${new Date(session.sessionDate).toISOString()}`;
  }

  getSessionLabel(session: PastSession): string {
    const date = DateTime.fromJSDate(new Date(session.sessionDate));
    return `${session.meetingName} — ${date.toLocaleString(DateTime.DATE_MED)}`;
  }
}
